/**
 * Core temperature, the vent, and the meltdown yield.
 *
 * The heat state label carries aria-live, not the number: announcing a ticking
 * value every frame is hostile to screen readers.
 */

import {
  greyMatterGain,
  heatState,
  brainShare,
  cellHeat,
  heatGen,
  ideaProgress,
  netHeat,
  voidHeat,
  voidState,
  secondsToMeltdown,
  thoughtsToFirstGM,
  type Modifiers,
} from '../../sim/formulas.ts';
import type { GameState } from '../../sim/state.ts';
import { createGauge, setGauge, type Gauge } from '../../render/gauge.ts';
import { byId, setDisabled, setText } from '../dom.ts';
import { formatDuration, formatNumber } from '../format.ts';

export interface MeltdownPanel {
  update(state: GameState, mods: Modifiers): void;
  gauge: Gauge;
}

export interface MeltdownHandlers {
  onVent(): void;
  onMeltdown(): void;
  onAutoMeltChange(enabled: boolean, threshold: number): void;
}

/** The void only earns a place in the breakdown once it is actually pushing. */
function extraHeatSource(state: GameState, mods: Modifiers): string {
  const fromVoid = voidHeat(state, mods);
  return fromVoid > 0.005 ? `   Void +${fromVoid.toFixed(2)}` : '';
}

export function createMeltdownPanel(handlers: MeltdownHandlers): MeltdownPanel {
  const gauge = createGauge(byId('gauge-mount'));

  const heatValue = byId('heat-value');
  const stateLabel = byId('heat-state');
  const ventButton = byId<HTMLButtonElement>('vent');
  const ventStatus = byId('vent-status');
  const yieldValue = byId('gm-yield');
  const yieldNote = byId('yield-note');
  const ideaBar = byId('idea-bar');
  const ideaNext = byId('idea-next');
  const meltButton = byId<HTMLButtonElement>('meltdown');
  const greyMatter = byId('grey-matter');
  const autoField = byId('automelt-field');
  const autoToggle = byId<HTMLInputElement>('automelt-toggle');
  const autoThreshold = byId<HTMLInputElement>('automelt-threshold');
  const voidPlate = byId('void-plate');
  const voidSplit = byId('void-split');
  const voidBar = byId('void-bar');
  const voidLabel = byId('void-state');
  const voidNote = byId('void-note');
  const heatSplit = byId('heat-split');

  ventButton.addEventListener('click', handlers.onVent);
  meltButton.addEventListener('click', handlers.onMeltdown);

  const pushAuto = (): void =>
    handlers.onAutoMeltChange(autoToggle.checked, Number(autoThreshold.value));
  autoToggle.addEventListener('change', pushAuto);
  autoThreshold.addEventListener('change', pushAuto);

  function update(state: GameState, mods: Modifiers): void {
    const format = state.settings.numberFormat;

    setGauge(gauge, state.heat);
    setText(heatValue, state.heat.toFixed(1));

    const status = heatState(state.heat);
    setText(stateLabel, status.charAt(0) + status.slice(1).toLowerCase());
    stateLabel.classList.toggle('is-caution', status === 'CAUTION');
    stateLabel.classList.toggle('is-critical', status === 'CRITICAL');

    // Vent.
    const ready = state.ventCooldown <= 0;
    setDisabled(ventButton, !ready);
    if (ready) {
      const eta = secondsToMeltdown(state, mods);
      setText(
        ventStatus,
        eta === null ? 'Ready — rig stable' : `Ready — meltdown in ${formatDuration(eta)}`,
      );
    } else {
      setText(ventStatus, `Recharging ${state.ventCooldown.toFixed(0)} s`);
    }

    // Where the heat is coming from. The cell line is the whole argument for
    // climbing the ladder, and it is invisible unless it is written down.
    const fromClock = heatGen(state.clock, mods);
    const fromCells = cellHeat(state);
    const net = netHeat(state, mods);
    const sign = (v: number): string => `${v >= 0 ? '+' : ''}${v.toFixed(2)}`;
    setText(
      heatSplit,
      `Clock ${sign(fromClock)}   Cells ${sign(fromCells)}` +
        `${extraHeatSource(state, mods)}   Cooling ${sign(-mods.cooling)}   Net ${sign(net)}`,
    );

    // The void. Hidden until it has actually started, so a new player is not
    // shown a panel that does nothing.
    const brain = brainShare(state, mods);
    const extraHeat = voidHeat(state, mods);
    const showVoid = state.unlocks.void === true;
    if (voidPlate.hidden === showVoid) voidPlate.hidden = !showVoid;
    if (showVoid) {
      setText(voidSplit, `${(brain * 100).toFixed(0)} / ${((1 - brain) * 100).toFixed(0)}`);
      const width = `${(brain * 100).toFixed(1)}%`;
      if (voidBar.style.width !== width) voidBar.style.width = width;

      const status = voidState(1 - brain);
      setText(voidLabel, status.charAt(0) + status.slice(1).toLowerCase());
      voidLabel.classList.toggle('is-caution', status === 'CROWDING');
      voidLabel.classList.toggle('is-critical', status === 'OVERWHELMING');
      setText(
        voidNote,
        extraHeat > 0
          ? `Holding them back is costing ${extraHeat.toFixed(2)} heat per second, and rising.`
          : 'They are gathering, but the brain still has the room.',
      );
    }

    // Yield, and how far the run is from the next whole idea.
    const gain = greyMatterGain(state, mods);
    const progress = ideaProgress(state, mods);
    setText(yieldValue, formatNumber(gain, format));
    setDisabled(meltButton, gain.lte(0));

    const width = `${(progress.fraction * 100).toFixed(1)}%`;
    if (ideaBar.style.width !== width) ideaBar.style.width = width;
    const carried = state.ideaRemainder;
    setText(
      ideaNext,
      `${formatNumber(progress.remaining.ceil(), format)} more sparks for ${
        gain.lte(0) ? 'the first idea' : `idea ${formatNumber(gain.add(1), format)}`
      }${carried.gt(0.005) ? ` — ${carried.toNumber().toFixed(2)} carried over` : ''}`,
    );

    if (gain.lte(0)) {
      const needed = thoughtsToFirstGM(state, mods);
      setText(
        yieldNote,
        `Not yet worth an idea. This run has to produce ` +
          `${formatNumber(needed.ceil(), format)} more sparks.`,
      );
    } else {
      setText(
        yieldNote,
        'Forcing it melts the brain. The run is destroyed; the idea is kept.',
      );
    }

    setText(greyMatter, formatNumber(state.greyMatter, format));

    // Failsafe, gated behind the automelt skill.
    const canAuto = mods.flags.automelt === true;
    if (autoField.hidden === canAuto) autoField.hidden = !canAuto;
    if (canAuto) {
      if (autoToggle.checked !== state.settings.autoMeltdownEnabled) {
        autoToggle.checked = state.settings.autoMeltdownEnabled;
      }
      const threshold = String(state.settings.autoMeltdownThreshold);
      if (autoThreshold.value !== threshold && document.activeElement !== autoThreshold) {
        autoThreshold.value = threshold;
      }
    }
  }

  return { update, gauge };
}
